// Settings › AI: the on/off switch, the instructions the AI reads first, and whether a key is
// set on the server (the key itself never lives in the database or on this page).

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, money, type MeResponse } from '../../api';
import { InlineError } from '../../components/States';
import { useToast } from '../../components/Toast';

const STARTER = `We are {company}, the RainSoft dealer for this area. We service, repair and install water treatment systems.
Hours: Monday to Friday 8 AM to 5 PM. Saturday by appointment.
Tone: friendly, short, no jargon. Sign off with the company name on a first message.
If someone asks for a price that is not on the Knowledge page, say a technician will confirm it.
If there is a leak: tell them to turn the bypass valve and call us. Nothing else.
To book a visit: ask for a good day and time, we will confirm by text.`;

export function AiSettings({ me }: { me: MeResponse }) {
  const qc = useQueryClient();
  const toast = useToast();
  const isAdmin = me.user.role === 'admin';
  const w = me.workspace;
  const [enabled, setEnabled] = useState(w.ai_enabled !== false);
  const [instructions, setInstructions] = useState(w.ai_instructions ?? '');
  const usage = useQuery({ queryKey: ['ai-usage'], queryFn: api.ai.usage, retry: false, staleTime: 30_000 });

  const save = useMutation({
    mutationFn: () => api.workspace.update({ ai_enabled: enabled, ai_instructions: instructions.trim() || null }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['me'] });
      toast({ text: 'Saved.', tone: 'ok' });
    },
  });

  const u = usage.data;
  const company = w.company_name ?? w.name;

  return (
    <div className="card">
      <h2>AI helper</h2>
      <p className="muted small">The AI suggests replies in the composer. It never sends on its own; a person clicks Send every time.</p>

      <div className="notice" style={{ marginBottom: 16 }}>
        {usage.isPending && <span className="muted">Checking the server…</span>}
        {usage.isError && <span className="warn-text">Could not check the AI status ({(usage.error as Error).message}).</span>}
        {u && u.configured ? (
          <>
            <strong>Set up.</strong> Provider: {u.provider ?? '?'} · model: <code>{u.model ?? '?'}</code>
            {u.enabled === false && <span className="warn-text"> · switched off below</span>}
            <div className="small" style={{ marginTop: 4 }}>
              This month: ${money(u.month?.cost_usd).toFixed(2)} · {u.month?.suggestions ?? 0} {u.month?.suggestions === 1 ? 'suggestion' : 'suggestions'}
            </div>
          </>
        ) : (
          u && (
            <>
              <strong>Not set up yet.</strong> Add <code>ANTHROPIC_API_KEY</code> (or <code>OPENAI_API_KEY</code>) to the server's <code>.env</code> and restart. The button in the composer will say so until then.
            </>
          )
        )}
      </div>

      <label className="check">
        <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} disabled={!isAdmin} />
        <span>
          <strong>AI helper on.</strong> Shows the AI button in the composer.
        </span>
      </label>

      <label className="field">
        <span>Instructions for the AI (who you are, hours, tone, what never to say)</span>
        <textarea className="input" value={instructions} onChange={(e) => setInstructions(e.target.value)} rows={9} disabled={!isAdmin} placeholder={STARTER.replace('{company}', company)} />
        <div className="field-help">
          Prices, FAQs and pictures live under <Link to="/settings/knowledge">Knowledge</Link>; the AI reads both. It only quotes prices from that page and never makes up an appointment time.
        </div>
      </label>
      {isAdmin && !instructions.trim() && (
        <button className="btn small" type="button" onClick={() => setInstructions(STARTER.replace('{company}', company))} style={{ marginBottom: 12 }}>
          Use the example as a start
        </button>
      )}
      <InlineError error={save.error} />
      {isAdmin && (
        <div>
          <button className="btn primary" disabled={save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? 'Saving' : 'Save'}
          </button>
        </div>
      )}
      {!isAdmin && <div className="muted small">Only an admin can change these.</div>}
    </div>
  );
}
